import { BlockBlobClient } from "@azure/storage-blob";
import { FileInsertSchema, FileSelectSchema } from "@shared-database/schema";
import { z } from "zod";
import { sanitizeFilenameForHeader } from "./sanitize-filename-for-header";

/**
 * Upload schema for a file is the same as insert, except we add a uploadUrl field
 */
export const FileUploadInsertSchema = FileInsertSchema.extend(
  z.object({ uploadUrl: z.string() }).shape,
);

/**
 * Upload schema for a file is the same as select, except we add a uploadUrl field
 */
export const FileUploadSelectSchema = FileSelectSchema.extend(
  z.object({ uploadUrl: z.string() }).shape,
);

/**
 * Upload type for a file is the same as insert, except we add a uploadUrl field
 */
export type FileUploadInsertType = z.infer<typeof FileUploadInsertSchema>;

/**
 * Upload type for a file is the same as select, except we add a uploadUrl field
 */
export type FileUploadSelectType = z.infer<typeof FileUploadSelectSchema>;

interface UploadFileParams {
  file: File;
  meta: FileUploadInsertType;
  onSuccess: () => void;
  onError: (error: unknown) => void;
  attachment: boolean;
}

/**
 * Uploads a file to the specified upload URL.
 *
 * @param params - The parameters for uploading the file.
 * @param params.file - The file to be uploaded.
 * @param params.meta - The metadata associated with the file.
 * @param params.onSuccess - The callback function to be called when the file is successfully uploaded.
 * @param params.onError - The callback function to be called when an error occurs during the upload process.
 */
export const uploadFile = async (params: UploadFileParams) => {
  try {
    const fileBuffer = await convertFileToArrayBuffer(params.file);

    if (fileBuffer === null || fileBuffer.byteLength < 1) {
      params.onError(new Error("Invalid file buffer."));
      return;
    }
    const blockBlobClient = new BlockBlobClient(params.meta.uploadUrl);
    await blockBlobClient.uploadData(fileBuffer, {
      blobHTTPHeaders: {
        blobContentType: params.meta.mimeType,
        blobCacheControl: "public, max-age=86400",
        blobContentDisposition: params.attachment
          ? `attachment; filename="${sanitizeFilenameForHeader(params.meta.name)}"`
          : undefined,
      },
    });

    params.onSuccess();
  } catch (error) {
    params.onError(error);
  }
};

/**
 * Converts a string to an ArrayBuffer.
 */
const convertStringToArrayBuffer = (str: string) => {
  const textEncoder = new TextEncoder();
  return textEncoder.encode(str).buffer;
};

/**
 * Converts a File object to an ArrayBuffer.
 *
 * @param file - The File object to convert.
 * @returns A Promise that resolves to the converted ArrayBuffer, or null if the conversion fails.
 * @throws An error if the file is invalid or cannot be read.
 */
export function convertFileToArrayBuffer(
  file: File,
): Promise<ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    if (!file || !file.name) {
      reject(new Error("Invalid or missing file."));
    }

    const reader = new FileReader();

    reader.onload = () => {
      const arrayBuffer: ArrayBuffer | null | string = reader.result;

      if (arrayBuffer === null) {
        resolve(null);
        return;
      }
      if (typeof arrayBuffer === "string") {
        resolve(convertStringToArrayBuffer(arrayBuffer));
        return;
      }
      if (!arrayBuffer) {
        reject(new Error("Failed to read file into ArrayBuffer."));
        return;
      }

      resolve(arrayBuffer);
    };

    reader.onerror = () => {
      reject(new Error("Error reading file."));
    };

    reader.readAsArrayBuffer(file);
  });
}
